import React, { useContext, useState } from "react";
import {
  BootstrapperContext,
  BootstrapperContextType,
} from "../bootstrapperContext";
import { ProductContextType, ProductRefType } from "./types";
import { ProductFamily } from "./product-family";
import { Product } from "./product";
import logging from "../../util/logging";

export const ProductContext = React.createContext({} as ProductContextType);

export default function ProductContextProvider({
  productRef,
  children,
}: React.PropsWithChildren<{
  productRef: ProductRefType | undefined;
}>): React.ReactElement {
  const log = logging("product context");
  const {
    families,
    allFamilies,
  }: {
    families: BootstrapperContextType["families"];
    allFamilies: BootstrapperContextType["allFamilies"];
  } = useContext(BootstrapperContext);

  /**
   * Default product is available when we have only one family available.
   */
  const defaultProduct = (): Product | undefined => {
    if (families.length !== 1) {
      return undefined;
    }

    return families[0].getDefaultProduct();
  };

  const findProductByRef = (
    productRef: ProductRefType | undefined,
  ): Product => {
    let foundProduct = undefined;

    if (productRef) {
      families.some((family: ProductFamily) => {
        const targetedProduct = family.getProduct(productRef);
        foundProduct = targetedProduct;
        return targetedProduct !== undefined;
      });
    }

    return foundProduct;
  };

  const findPersistedProductByRef = (
    productRef: ProductRefType | undefined,
  ): Product => {
    let foundProduct = undefined;

    if (productRef !== undefined) {
      allFamilies.some((family: ProductFamily) => {
        const targetedProduct = family.getProduct(productRef);
        foundProduct = targetedProduct;
        return targetedProduct !== undefined;
      });
    }

    return foundProduct;
  };

  /**
   * Sets the product to the default for the family iff there is only one family selected, and no product selected.
   * Do nothing otherwise.
   */
  const resetProductToDefaultIfUndefined = (): void => {
    if (selectedProduct === undefined && families.length === 1) {
      log.warn(
        `findProduct was unable to resolve the product it was request to. Given: ${productRef}. Falling back to the default`,
      );
      setSelectedProduct(defaultProduct());
    }
  };

  /**
   * Find the product.
   *
   * @param productRef
   */
  const findProductIfValid = (
    productRef: ProductRefType | undefined,
  ): Product => {
    const foundProduct = findProductByRef(productRef);

    if (foundProduct === undefined) {
      log.warn(
        `findProduct was unable to resolve the product it was request to. Given: ${productRef}`,
      );
    }

    return foundProduct;
  };

  const findFamily = (name: ProductFamily["name"]): ProductFamily => {
    const foundFamily = families.find((family: ProductFamily) => {
      return family.name === name;
    });

    if (foundFamily === undefined) {
      throw new Error(
        `findFamily was unable to resolve the request family. Family name: ${name}`,
      );
    }

    return foundFamily;
  };

  const findFamilyFromAll = (name: ProductFamily["name"]): ProductFamily => {
    const foundFamily = allFamilies.find((family: ProductFamily) => {
      return family.name === name;
    });

    if (foundFamily === undefined) {
      throw new Error(
        `findFamily was unable to resolve the request family. Family name: ${name}`,
      );
    }

    return foundFamily;
  };

  /**
   * Given the new product to be selected in the application; update the hierarchies visibility.
   * That means if a hidden product is selected, all other hidden products in the family become visible.
   * The opposite applies as well.
   *
   * @param newProduct
   * @param skipSync
   */
  const syncVisibilityInHierarchy = (
    newProduct: Product,
    skipSync = false,
  ): void => {
    if (!skipSync && !!newProduct) {
      newProduct.family.syncProductsVisibility(newProduct);
    }
    setSelectedProduct(newProduct);
  };

  const [selectedProduct, setSelectedProduct] = useState<Product | undefined>(
    findProductByRef(productRef),
  );

  return (
    <ProductContext.Provider
      value={{
        setSelectedProduct: syncVisibilityInHierarchy,
        selectedProduct,
        findProduct: findProductByRef,
        findPersistedProductByRef,
        findFamily,
        findFamilyFromAll,
        findProductIfValid,
        resetProductToDefault: resetProductToDefaultIfUndefined,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
}
